/**
 * Edit Member Consent: the three flags, and the two addresses they are useless
 * without.
 *
 * Ported from `legacy/frontendx/src/apps/mrm/components/MrmRowDetailView/
 * MrmConsentEditModal.tsx`, in its order: the email above the box that consents
 * to it, the mobile above its own, and "only important updates" under both.
 * That pairing is the point — a tick with no address to send to is the one
 * mistake this form can make, and the field it needs is the line above it.
 *
 * The generic `EditDialog` drew this from field metadata before: five stacked
 * controls, the mobile a bare text box holding `+639171234567`, and a consent
 * ticked with nothing to send to only refused by the server a second later.
 * The rules between the five are the whole form, so this one is written out.
 *
 * The rules are the service's, said here first so the round trip is not the
 * thing that says them (`_assert_reachable` in the member handler):
 *
 *  - email consent needs an email;
 *  - mobile consent needs a mobile, and so does "only important updates" —
 *    those updates go out as SMS;
 *  - and with no consent at all one of the two is still needed, because a
 *    membership nobody can reach is one nobody can renew.
 *
 * An address already on file is shown but not editable. Consent is a question
 * about channels, not the place a member's contact details are corrected —
 * that is Basic Information, which keeps its own history of the change.
 */

import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Checkbox, Field, Form, Input } from "@/components/ui/field";
import type { TranslationKey } from "@/constants/i18n";
import type { Member } from "@/pages/MembersPage";
import { api, errorMessage, ifMatch } from "@/services/api";
import { useTranslation } from "@/store/locale";
import {
  isValidEmail,
  isValidMobile,
  localMobile,
  sanitizeMobile,
  toApiMobile,
} from "@/utils/contact";

/** What the form can complain about, per field. Both may be wrong at once. */
interface Complaints {
  email?: TranslationKey;
  mobile?: TranslationKey;
}

export function ConsentDialog({
  member,
  onClose,
  onSaved,
}: {
  member: Member;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();

  // What the member is on record with, as the form deals in it: the mobile as
  // its ten local digits, both trimmed, neither ever null.
  const initialEmail = (member.email ?? "").trim();
  const initialMobile = localMobile(member.mobile_number);
  // An address on file is a fact about the member, not a blank to fill in here.
  const emailLocked = initialEmail !== "";
  const mobileLocked = initialMobile !== "";

  const [emailConsent, setEmailConsent] = useState(member.email_consent);
  const [mobileConsent, setMobileConsent] = useState(member.phone_consent);
  const [importantOnly, setImportantOnly] = useState(member.only_important_updates);
  const [email, setEmail] = useState(initialEmail);
  const [mobile, setMobile] = useState(initialMobile);
  const [bad, setBad] = useState<Complaints>({});
  const [error, setError] = useState<string | null>(null);

  // Which addresses this form is allowed to ask for, and therefore which ones
  // carry an asterisk. The mobile answers for two of the three flags: the
  // important updates go out by SMS.
  const emailRequired = emailConsent;
  const mobileRequired = mobileConsent || importantOnly;

  // Nothing moved, nothing to save — legacy held Submit shut on exactly this,
  // and a locked field cannot be part of the difference.
  const changed =
    emailConsent !== member.email_consent ||
    mobileConsent !== member.phone_consent ||
    importantOnly !== member.only_important_updates ||
    (!emailLocked && email.trim() !== initialEmail) ||
    (!mobileLocked && mobile !== initialMobile);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/members/${member.id}/consent`, body, ifMatch(member)),
    onSuccess: () => {
      toast.success(t("md.consentUpdated"));
      onSaved();
    },
    onError: (err) => setError(errorMessage(err, t("md.consentFailed"))),
  });

  /** Every rule at once, so a form with two things wrong says both. */
  const check = (): Complaints => {
    const trimmed = email.trim();
    const complaints: Complaints = {};
    if (emailRequired && !trimmed) complaints.email = "new.emailRequired";
    else if (trimmed && !isValidEmail(trimmed)) complaints.email = "md.consentEmailInvalid";
    if (mobileRequired && !mobile) complaints.mobile = "new.mobileRequired";
    else if (mobile && !isValidMobile(mobile)) complaints.mobile = "md.consentMobileDigits";
    // Consenting to nothing is allowed; being unreachable is not.
    if (!trimmed && !mobile) {
      complaints.email ??= "new.contactRequired";
      complaints.mobile ??= "new.contactRequired";
    }
    return complaints;
  };

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    setError(null);
    const complaints = check();
    setBad(complaints);
    if (complaints.email || complaints.mobile) return;
    save.mutate({
      email_consent: emailConsent,
      phone_consent: mobileConsent,
      only_important_updates: importantOnly,
      // A locked address is left out rather than sent back unchanged: the
      // update is partial, so what is not sent is what the member already has,
      // and this form has no business rewriting either of them.
      ...(emailLocked ? {} : { email: email.trim() || null }),
      ...(mobileLocked ? {} : { mobile_number: toApiMobile(mobile) }),
    });
  };

  // Unique, and pressed directly. A footer button reaches its form across the
  // sheet by `form="…"`, which the browser resolves against the whole document
  // — so a literal shared with another dialog submits whichever rendered first.
  const formId = useId();
  return (
    <Modal
      open
      onClose={onClose}
      title={t("md.editMemberConsent")}
      // Legacy's 560px.
      className="sm:max-w-[35rem]"
      footer={
        <>
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => submit()}
            className="flex-1"
            loading={save.isPending}
            loadingLabel={t("common.saving")}
            disabled={!changed}
          >
            {t("common.submit")}
          </Button>
        </>
      }
    >
      <Form id={formId} className="grid gap-4 pb-2" onSubmit={submit}>
        <Field
          label={t("mem.emailAddress")}
          htmlFor="consent-email"
          required={emailRequired}
          error={bad.email ? t(bad.email) : undefined}
          hint={emailLocked ? t("md.consentLocked") : undefined}
        >
          <Input
            id="consent-email"
            type="email"
            maxLength={254}
            autoComplete="off"
            placeholder={t("new.enterEmail")}
            value={email}
            disabled={emailLocked}
            readOnly={emailLocked}
            aria-invalid={bad.email ? true : undefined}
            onChange={(event) => {
              setEmail(event.target.value);
              // Once it has been refused, the complaint goes as soon as the
              // field is touched — it is re-judged on Submit, not per keystroke,
              // so a half-typed address is not called invalid.
              if (bad.email) setBad((previous) => ({ ...previous, email: undefined }));
            }}
          />
        </Field>

        <Checkbox
          id="consent-email-flag"
          checked={emailConsent}
          label={t("mem.emailConsent")}
          onChange={(event) => {
            setEmailConsent(event.target.checked);
            if (!event.target.checked)
              setBad((previous) => ({ ...previous, email: undefined }));
          }}
        />

        <Field
          label={t("mem.mobileNumber")}
          htmlFor="consent-mobile"
          required={mobileRequired}
          error={bad.mobile ? t(bad.mobile) : undefined}
          hint={mobileLocked ? t("md.consentLocked") : undefined}
        >
          {/* The country code is printed beside the box rather than typed into
              it: the API stores every mobile under +63, so it is a fact about
              the field and not ten digits' worth of somebody's typing. */}
          <div className="flex">
            <span
              aria-hidden
              className="rounded-field border-line bg-surface-sunken text-fg text-body inline-flex items-center rounded-r-none border border-r-0 px-3.5"
            >
              +63
            </span>
            <Input
              id="consent-mobile"
              type="tel"
              inputMode="numeric"
              autoComplete="off"
              maxLength={10}
              className="rounded-l-none"
              placeholder="9XXXXXXXXX"
              value={mobile}
              disabled={mobileLocked}
              readOnly={mobileLocked}
              aria-invalid={bad.mobile ? true : undefined}
              onChange={(event) => {
                setMobile(sanitizeMobile(event.target.value));
                if (bad.mobile) setBad((previous) => ({ ...previous, mobile: undefined }));
              }}
            />
          </div>
        </Field>

        <Checkbox
          id="consent-mobile-flag"
          checked={mobileConsent}
          label={t("md.mobileConsent")}
          onChange={(event) => {
            setMobileConsent(event.target.checked);
            if (!event.target.checked)
              setBad((previous) => ({ ...previous, mobile: undefined }));
          }}
        />

        <Checkbox
          id="consent-important-only"
          checked={importantOnly}
          label={t("mem.onlyImportantUpdates")}
          onChange={(event) => {
            setImportantOnly(event.target.checked);
            if (!event.target.checked)
              setBad((previous) => ({ ...previous, mobile: undefined }));
          }}
        />

        {error ? (
          <p role="alert" className="text-danger text-callout">
            {error}
          </p>
        ) : null}
      </Form>
    </Modal>
  );
}
